// decision-policy-authority-reader.ts — V2-1 (spec V2 §4.1 "Nguồn thẩm quyền").
//
// `decision_policy` hiệu lực của mission, đọc LẠI ở mỗi lần xét (không cache):
//  1. G0 phải `approved` — KHÔNG nhận `waived` (không dùng
//     `APPROVED_GATE_STATUSES`, vốn coi waived là có thẩm quyền), có
//     `artifact_hash`, `request_id` và `blueprint-binding.json`.
//  2. Bytes thẩm quyền = bytes có sha256 == `g0.artifact_hash`: snapshot control
//     root → `HEAD:path` → lịch sử HEAD (g0-approved-blueprint-bytes-lookup.ts).
//     File live trong worktree KHÔNG BAO GIỜ là nguồn.
//  3. BLUEPRINT hiện tại (worktree + HEAD) phải còn hash đó; lệch ⇒ `ask`
//     (recheck G0 trước dispatch kế tiếp invalidate như v1).
// Mọi nhánh lỗi ⇒ `ask` kèm lý do (dùng cho dòng "không auto vì …").
import { computeBlueprintHash, sha256HexOfBytes } from "./blueprint-gate-hash.js";
import {
  type ParsedDecisionPolicy,
  parseDecisionPolicyFromBlueprint,
} from "./decision-policy-blueprint-line-parser.js";
import { loadBlueprintBytesWithHash } from "./g0-approved-blueprint-bytes-lookup.js";
import { readGateFile } from "./gate-registry.js";
import {
  readMissionBlueprintBinding,
  resolveBlueprintPathInWorktree,
} from "./mission-blueprint-binding.js";
import { loadCommittedBlueprintBytes } from "./mission-blueprint-committed-bytes-check.js";

export interface AuthorizedBlueprint {
  g0RequestId: string;
  blueprintHash: string;
  sourcePath: string;
  policy: ParsedDecisionPolicy;
}

export type AuthorizedBlueprintRead =
  | { ok: true; blueprint: AuthorizedBlueprint }
  | { ok: false; reason: string };

export type DecisionPolicyAuthority =
  | { policy: "auto"; g0RequestId: string; blueprintHash: string }
  | { policy: "ask"; reason: string; warning?: string };

/** Bước 1–2: G0 + bytes đã hash. Không đụng worktree live (status dùng được). */
export async function readAuthorizedBlueprint(
  controlRoot: string,
  repoDir: string,
): Promise<AuthorizedBlueprintRead> {
  const g0 = (await readGateFile(controlRoot)).g0;
  if (g0.status !== "approved")
    return { ok: false, reason: `G0 đang ${g0.status}, không approved` };
  if (g0.artifact_hash === undefined) return { ok: false, reason: "G0 thiếu artifact_hash" };
  if (g0.request_id === undefined) return { ok: false, reason: "G0 thiếu request_id" };
  const binding = await readMissionBlueprintBinding(controlRoot);
  if (binding === undefined) return { ok: false, reason: "thiếu blueprint-binding.json" };

  const hash = g0.artifact_hash.toLowerCase();
  const bytes = await loadBlueprintBytesWithHash(controlRoot, repoDir, binding.source_path, hash);
  if (bytes === undefined) {
    return { ok: false, reason: "không có bytes BLUEPRINT khớp hash G0 (snapshot/git)" };
  }
  return {
    ok: true,
    blueprint: {
      g0RequestId: g0.request_id,
      blueprintHash: hash,
      sourcePath: binding.source_path,
      policy: parseDecisionPolicyFromBlueprint(bytes.toString("utf8")),
    },
  };
}

/** Bước 3: BLUEPRINT ở worktree (và HEAD nếu có) còn đúng hash đã duyệt. */
async function currentBlueprintProblem(
  repoDir: string,
  sourcePath: string,
  hash: string,
): Promise<string | undefined> {
  const live = await computeBlueprintHash(resolveBlueprintPathInWorktree(repoDir, sourcePath));
  if (live !== hash) return "BLUEPRINT trong worktree khác bytes đã duyệt G0";
  const head = await loadCommittedBlueprintBytes(repoDir, sourcePath, "HEAD");
  if (head !== undefined && sha256HexOfBytes(head) !== hash) {
    return "BLUEPRINT ở HEAD khác bytes đã duyệt G0";
  }
  return undefined;
}

export async function readDecisionPolicyAuthority(
  controlRoot: string,
  repoDir: string,
): Promise<DecisionPolicyAuthority> {
  try {
    const read = await readAuthorizedBlueprint(controlRoot, repoDir);
    if (!read.ok) return { policy: "ask", reason: read.reason };
    const { blueprint } = read;
    if (blueprint.policy.policy !== "auto") {
      return {
        policy: "ask",
        reason:
          blueprint.policy.warning ?? "BLUEPRINT đã duyệt không ủy nhiệm (decision_policy: ask)",
        ...(blueprint.policy.warning !== undefined ? { warning: blueprint.policy.warning } : {}),
      };
    }
    const drift = await currentBlueprintProblem(
      repoDir,
      blueprint.sourcePath,
      blueprint.blueprintHash,
    );
    if (drift !== undefined) return { policy: "ask", reason: drift };
    return {
      policy: "auto",
      g0RequestId: blueprint.g0RequestId,
      blueprintHash: blueprint.blueprintHash,
    };
  } catch (err) {
    return { policy: "ask", reason: `không đọc được thẩm quyền G0 (${(err as Error).message})` };
  }
}

/**
 * Cảnh báo cho `status` (§4.1 "parse lỗi hoặc giá trị lạ ⇒ ask + cảnh báo"):
 * chỉ từ bytes ĐÃ DUYỆT, không bao giờ từ file live. Mọi lỗi đọc ⇒ không cảnh báo.
 */
export async function readDecisionPolicyStatusWarning(
  controlRoot: string,
  repoDir: string,
): Promise<string | undefined> {
  try {
    const read = await readAuthorizedBlueprint(controlRoot, repoDir);
    return read.ok ? read.blueprint.policy.warning : undefined;
  } catch {
    return undefined;
  }
}
